import { Download, Renew } from '@carbon/icons-react'
import React, { useEffect } from 'react'
import {
  getAllQueryParamsAsObjectFromUrl,
  replaceUrl
} from '../../../utils/UrlUtils'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { AccessRoleConstants } from '../../../constants/AccessRoleConstants'
import AllExceptions from './Screens/AllExceptions'
import { Button } from '../../../components/New/Button/Button'
import { DDSTypography } from 'den-design-system'
import DocumentHeader from '../../../components/Helmet/DocumentHeader'
import GroupedExceptions from './Screens/GroupedExceptions'
import { HeaderConstants } from '../../../constants/HeaderConstants'
import { ProjectType } from '../../../enum/ProjectType.enum'
import SecureComponent from '../../../components/Layout/SecureComponent'
import StringConstants from '../../../constants/StringConstants'
import Tabs from '../../../components/common/DDS/Tabs/Tabs'
import Timezone from '../../../components/common/Timezone/Timezone'
import TimezoneConstants from '../../../constants/TimezoneConstants'
import { convertJsonToCsvDownload } from '../../../utils/FileUtils'
import { downloadGroupedExceptions } from '../../../api/exception/Exception.service'
import { gaPageConstants } from '../../../constants/GAConstants'
import { getOrgTimeRange } from '../../../utils/DateTimeUtils'
import { handleNavigationBasedOnProjectCount } from '../../../utils/SidebarUtils'
import { observer } from 'mobx-react-lite'
import pallete from '../../../global/newPallete'
import routeConstants from '../../../constants/RouteConstants'
import { useAnalyticsEventTracker } from '../../../utils/GAUtils'
import { useStoreContext } from '../../../store/StoreContext'

const Exceptions: React.FC = () => {
  //API call related functions
  const [tabIndex, setTabIndex] = React.useState<string>('1')
  const [searchParams, setSearchParams] = useSearchParams()
  const store = useStoreContext()
  const navigate = useNavigate()
  const projectType = store.scopeStore.getSelectedProject().type
  const isFullStackProject = projectType === ProjectType.FULL_STACK_ONLY
  const gaEventTracker = useAnalyticsEventTracker(
    gaPageConstants.REFRESH_EXCEPTIONS_DASHBOARD
  )

  const organizationPreference =
    store.userStore.getOrganizationPreference()?.timeZone
  const matchedTimeZone = TimezoneConstants.find(
    (tz) => tz?.tzCode === organizationPreference
  )

  useEffect(() => {
    handleNavigationBasedOnProjectCount(
      StringConstants.TAB_VALUES.EXCEPTION,
      store,
      navigate
    )
  }, [])

  const downloadRecords = () => {
    const allQueryParams = getAllQueryParamsAsObjectFromUrl(location.search)

    const queryParamsForDownload = {
      ...allQueryParams,

      'time-zone': store.userStore.getOrganizationPreference().timeZone
    }

    downloadGroupedExceptions(queryParamsForDownload)
      .then((response: any) => {
        convertJsonToCsvDownload(response.data, 'Grouped_Exception_Data.csv')
      })
      .finally(() => {
        store.uiStore.setGlobalLoader(false)
        store.exceptionStore.setLoading(false)
      })
  }

  useEffect(() => {
    sessionStorage.setItem(
      StringConstants.ACTIVE_TAB,
      StringConstants.TAB_VALUES.EXCEPTION
    )
  }, [])
  useEffect(() => {
    const tabFromParams = searchParams.get('tab')
    const allQueryParams = getAllQueryParamsAsObjectFromUrl(location.search)
    if (tabFromParams) {
      setTabIndex(tabFromParams.toString())
    } else {
      setSearchParams({ ...allQueryParams, tab: '1' })
    }
    if (tabIndex === '1') {
      store.breadcrumbStore.setBreadcrumbsOptions({
        label: isFullStackProject ? 'Grouped Exceptions' : 'Grouped Errors',
        link:
          replaceUrl(
            routeConstants.EXCEPTIONS,
            store.scopeStore.getScope()['organization-id'] as string,
            store.scopeStore.getScope()['project-id'] as string,
            store.scopeStore.getScope()['environment-id'] as string
          ) + '?tab=1'
      })
    } else if (tabIndex === '2') {
      store.breadcrumbStore.setBreadcrumbsOptions({
        label: isFullStackProject ? 'All Exceptions' : 'All Errors',
        link:
          replaceUrl(
            routeConstants.EXCEPTIONS,
            store.scopeStore.getScope()['organization-id'] as string,
            store.scopeStore.getScope()['project-id'] as string,
            store.scopeStore.getScope()['environment-id'] as string
          ) + '?tab=2'
      })
    }
  }, [tabIndex, store.breadcrumbStore.getBreadcrumbsOptions(), projectType])
  const tabOptions = [
    {
      label:
        tabIndex === '1' ? (
          <DDSTypography.Title
            type='h5'
            variant='semiBold'
            color={pallete.colors.primary2}
          >
            {store.scopeStore.getSelectedProject().type ===
            ProjectType.FULL_STACK_ONLY
              ? 'Grouped Exceptions'
              : 'Grouped Errors'}
          </DDSTypography.Title>
        ) : (
          <DDSTypography.Title
            type='h5'
            variant='medium'
            color={pallete.colors.textDark5}
          >
            {store.scopeStore.getSelectedProject().type ===
            ProjectType.FULL_STACK_ONLY
              ? 'Grouped Exceptions'
              : 'Grouped Errors'}
          </DDSTypography.Title>
        ),
      children: <GroupedExceptions />
    },
    {
      label:
        tabIndex === '2' ? (
          <DDSTypography.Title
            type='h5'
            variant='semiBold'
            color={pallete.colors.primary2}
          >
            {store.scopeStore.getSelectedProject().type ===
            ProjectType.FULL_STACK_ONLY
              ? 'All Exceptions'
              : 'All Errors'}
          </DDSTypography.Title>
        ) : (
          <DDSTypography.Title
            type='h5'
            variant='medium'
            color={pallete.colors.textDark5}
          >
            {store.scopeStore.getSelectedProject().type ===
            ProjectType.FULL_STACK_ONLY
              ? 'All Exceptions'
              : 'All Errors'}
          </DDSTypography.Title>
        ),
      children: <AllExceptions />
    }
  ]
  return (
    <div className='flex flex-col w-full relative'>
      <DocumentHeader
        header={
          store.scopeStore.getSelectedProject().type ===
          ProjectType.FULL_STACK_ONLY
            ? HeaderConstants.EXCEPTIONS
            : HeaderConstants.ERROR
        }
      />
      <div className='w-full justify-between relative'>
        <Tabs
          tabOptions={tabOptions}
          className='w-max justify-center'
          id='tabs-grouped-exceptions'
          onChange={(index: any) => {
            if (index !== tabIndex) {
              setTabIndex(index.toString())
              store.exceptionsStoreV2.setCurrentTab(index)

              const timeZone =
                store.userStore.getOrganizationPreference()?.timeZone
              const { startOfMonth, endOfCurrentDayTimestamp } =
                getOrgTimeRange(timeZone)

              const currentSearchParams = getAllQueryParamsAsObjectFromUrl(
                location.search
              )

              const newSearchParams: any = {
                ...currentSearchParams,
                page: '1',
                'created-from':
                  currentSearchParams['created-from'] || String(startOfMonth),
                'created-to':
                  currentSearchParams['created-to'] ||
                  String(endOfCurrentDayTimestamp),
                'time-zone': timeZone,
                tab: index.toString()
              }
              Object.keys(newSearchParams).forEach((key) => {
                if (key.toLowerCase().includes('search')) {
                  delete newSearchParams[key]
                }
              })

              if (index === 0) {
                newSearchParams.status = 'active'
              } else if (index === 1) {
                newSearchParams.status = 'inactive'
              } else {
                delete newSearchParams.status
              }

              setSearchParams(newSearchParams)
            }
          }}
          disabled={store.uiStore.getGlobalLoader()}
          tabOptionsStyle={{
            minWidth: 'fit-content',
            width: 'auto',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '8px 12px',
            marginRight: '8px'
          }}
          activeTab={tabIndex}
        />

        <div className='flex flex-row gap-x-[8px] absolute top-[4px] right-0'>
          <div>
            <Timezone
              tooltipText='Time zone is based on organization settings'
              text={
                <div>
                  {matchedTimeZone?.tzCode} {matchedTimeZone?.label}
                </div>
              }
              textColor={pallete.colors.neuButtonText}
            />
          </div>

          <Button
            id='refresh-button'
            type='neutral'
            size='small'
            disabled={
              store.uiStore.getGlobalLoader() ||
              store.exceptionStore.getLoading()
            }
            startIcon={<Renew color={pallete.colors.textDark10} />}
            onClick={() => {
              gaEventTracker(gaPageConstants.REFRESH_EXCEPTIONS_DASHBOARD)
              store.exceptionStore.setIsRefreshed(true)
            }}
          />
          {/* {tabIndex === '1' && (
            <Button
              id='export to csv'
              label={
                <DDSTypography.Paragraph
                  variant='semiBold'
                  size='para'
                  className='text-center whitespace-nowrap flex justify-center '
                  color={pallete.colors.textDark10}
                >
                  Export to CSV
                </DDSTypography.Paragraph>
              }
              type='neutral'
              disabled={
                store.exceptionsStoreV2.getTotalCount() === 0 ||
                store.userStore.getNoActiveSubscription() ||
                store.uiStore.getGlobalLoader() ||
                store.exceptionStore.getLoading()
              }
              size='small'
              startIcon={<Download color={pallete.colors.neuButtonText} />}
              onClick={() => downloadRecords()}
            />
          )} */}
        </div>
      </div>
    </div>
  )
}

export default SecureComponent(
  observer(Exceptions),
  AccessRoleConstants.MEMBER_LEVEL
)
